"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/spacing.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { motion } from "motion/react";
import { UnifiedModal, useModalManager } from "./UnifiedModal";
import Link from "next/link";

interface CTAProps {
  title?: string;
  description?: string;
  primaryButtonText?: string;
  className?: string;
  onPrimaryClick?: () => void;
  type?: "default" | "enterprise";
}

export const CTA = ({
  title,
  description,
  primaryButtonText,
  className = "",
  onPrimaryClick,
  type = "default",
}: CTAProps) => {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  // Set defaults based on type
  const getDefaults = () => {
    switch (type) {
      case "enterprise":
        return {
          title: title || "Ready to elevate your event strategy?",
          description:
            description ||
            "Let's craft the infrastructure behind your most important meetings - so you can focus on what matters: the people, the purpose, and the outcomes.",
          primaryButtonText: primaryButtonText || "Request a demo",
        };
      default:
        return {
          title: title || "Ready to streamline your events?",
          description: description || "Get started with Nowadays today and see the difference AI can make.",
          primaryButtonText: primaryButtonText || "Get Started",
        };
    }
  };

  const defaults = getDefaults();

  const handlePrimaryClick = () => {
    if (onPrimaryClick) {
      onPrimaryClick();
    } else {
      // Default behavior: open demo booking modal
      openModal("demo");
    }
  };

  return (
    <div className={`w-full flex flex-col gap-6 md:gap-8 md:text-center py-12 md:py-24 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h3 className="text-h3 text-landing-primary mb-6 text-left md:text-center">{defaults.title}</h3>
        {defaults.description && (
          <p className="text-body text-landing-secondary md:max-w-lg md:mx-auto mb-8 text-left md:text-center">
            {defaults.description}
          </p>
        )}
        <div className="flex md:justify-center gap-3">
          <Button variant="primary" size="md" onClick={handlePrimaryClick}>
            {defaults.primaryButtonText}
          </Button>
          <Link href="/create">
            <Button variant="outline" color="neutral" size="md">
              Create an Event
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title={type === "enterprise" ? "Enterprise solutions demo" : "Book a Demo"}
        subtitle={
          type === "enterprise"
            ? "Schedule a call to discuss your enterprise event needs"
            : "Schedule a 30-minute demo with our team"
        }
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText={type === "enterprise" ? "Book your enterprise demo" : "Book your demo with our team"}
      />
    </div>
  );
};
